// Inkhunt design tokens, shared by the web (MUI theme + CSS variables) and the mobile app
// (React Native StyleSheets). Pure data: no platform code, no behaviour. Both clients derive
// their theme from here so the two products cannot drift apart. The rationale for each value
// lives in apps/web/DESIGN.md.

export type ColorScheme = "light" | "dark";

export interface Palette {
  /** Page background. */
  canvas: string;
  /** Alternate band (profile header, footer-like areas). The colour change is the divider. */
  canvasAlt: string;
  /** The hero tile: the studio photo's own backdrop, so the photo bleeds into the page. */
  canvasHero: string;
  /** Raised surfaces that sit above the canvas: menus, dialogs, cards. */
  surface: string;
  /** Quiet fills: search field, segmented-control track, image placeholders. */
  fill: string;
  fillStrong: string;
  /** Primary text and the ink of every action. */
  ink: string;
  /** Secondary text, captions, placeholders. AA on every canvas and fill. */
  inkSecondary: string;
  /** Hairlines between groups. Decorative only, so no contrast requirement. */
  line: string;
  /** Control boundaries (inputs, outlined buttons). >= 3:1 against the canvas. */
  lineStrong: string;
  /** Filled pill buttons. */
  action: string;
  actionHover: string;
  onAction: string;
  /** Flash red: marks state (active, liked, located). Never body text, never a link. */
  accent: string;
  accentSoft: string;
  onAccent: string;
  danger: string;
  dangerSoft: string;
  success: string;
  warning: string;
  warningSoft: string;
  /** Keyboard focus ring. */
  focus: string;
  /** Translucent navigation bar, used under a backdrop blur. */
  nav: string;
  /** Lightbox backdrop for the artwork viewer. Same near-black void in both schemes. */
  scrim: string;
  selection: string;
}

export const palettes: Record<ColorScheme, Palette> = {
  light: {
    canvas: "#ffffff",
    canvasAlt: "#f5f3f1",
    canvasHero: "#e9e5e2",
    surface: "#ffffff",
    fill: "#efecea",
    fillStrong: "#e4e0dd",
    ink: "#1c1b1a",
    inkSecondary: "#64605d",
    line: "rgba(28, 27, 26, 0.1)",
    lineStrong: "rgba(28, 27, 26, 0.5)",
    action: "#1c1b1a",
    actionHover: "#3a3735",
    onAction: "#ffffff",
    accent: "#c8102e",
    accentSoft: "rgba(200, 16, 46, 0.1)",
    onAccent: "#ffffff",
    danger: "#c8102e",
    dangerSoft: "rgba(200, 16, 46, 0.08)",
    success: "#1d7a45",
    warning: "#7a5200",
    warningSoft: "#fbf1dc",
    focus: "#1c1b1a",
    nav: "rgba(255, 255, 255, 0.78)",
    scrim: "rgba(8, 8, 8, 0.92)",
    selection: "rgba(200, 16, 46, 0.16)",
  },
  dark: {
    canvas: "#0c0b0b",
    canvasAlt: "#161514",
    canvasHero: "#0c0b0b",
    surface: "#1e1c1b",
    fill: "#252321",
    fillStrong: "#2f2c2a",
    ink: "#f4f2f0",
    inkSecondary: "#a8a39f",
    line: "rgba(244, 242, 240, 0.12)",
    lineStrong: "rgba(244, 242, 240, 0.42)",
    action: "#f4f2f0",
    actionHover: "#dcd8d4",
    onAction: "#0c0b0b",
    accent: "#ff4d5e",
    accentSoft: "rgba(255, 77, 94, 0.14)",
    onAccent: "#0c0b0b",
    danger: "#ff4d5e",
    dangerSoft: "rgba(255, 77, 94, 0.1)",
    success: "#5fd08a",
    warning: "#f0c060",
    warningSoft: "rgba(240, 192, 96, 0.12)",
    focus: "#f4f2f0",
    nav: "rgba(12, 11, 11, 0.72)",
    scrim: "rgba(8, 8, 8, 0.92)",
    selection: "rgba(255, 77, 94, 0.28)",
  },
};

/**
 * Type scale. `min` is the phone size (and the mobile app's size), `max` the desktop size; the
 * web interpolates between them with clamp(). Tracking is in em so it scales with the size.
 * The weight ladder is 400 (reads), 500 (controls), 600 (speaks). No 700: Geist's 600 already
 * carries a headline.
 */
export interface TypeToken {
  min: number;
  max: number;
  lineHeight: number;
  weight: 400 | 500 | 600;
  tracking: number;
}

export const typeScale = {
  /** The wordmark moment on the welcome hero. Capped at 6rem. */
  hero: { min: 52, max: 96, lineHeight: 1, weight: 600, tracking: -0.035 },
  /** Page titles: "login", "settings", a profile name. */
  title: { min: 32, max: 48, lineHeight: 1.08, weight: 600, tracking: -0.03 },
  /** Section heads and empty states. */
  headline: { min: 24, max: 32, lineHeight: 1.15, weight: 600, tracking: -0.022 },
  /** The nav wordmark, dialog titles, stat numbers. */
  tagline: { min: 19, max: 21, lineHeight: 1.25, weight: 600, tracking: -0.018 },
  /** Hero subcopy and intro paragraphs. */
  lead: { min: 19, max: 24, lineHeight: 1.35, weight: 400, tracking: -0.012 },
  /** Running text. 17px, not 16: the extra pixel sets a reading pace. */
  body: { min: 17, max: 17, lineHeight: 1.5, weight: 400, tracking: -0.011 },
  /** Secondary running text: bios, artwork captions. */
  callout: { min: 15, max: 15, lineHeight: 1.45, weight: 400, tracking: -0.006 },
  /** Buttons, nav links, tabs, form labels. */
  label: { min: 15, max: 15, lineHeight: 1.2, weight: 500, tracking: -0.006 },
  /** Metadata, counts, helper and error text. */
  caption: { min: 13, max: 13, lineHeight: 1.38, weight: 400, tracking: 0 },
} satisfies Record<string, TypeToken>;

export type TypeRole = keyof typeof typeScale;

export const fontFamily = {
  name: "Geist",
  /** Web stack: Geist first, then the platform face it most resembles. */
  webStack:
    '"Geist", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
} as const;

/** 4px base, structural steps on 8. */
export const space = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
  xxxl: 64,
  section: 96,
} as const;

/**
 * One radius grammar. Photos and inputs take md, cards and dialogs lg, anything that acts
 * (buttons, chips, the search field, the segmented control) is a pill.
 */
export const radius = {
  xs: 6,
  sm: 8,
  md: 12,
  lg: 18,
  xl: 24,
  pill: 999,
} as const;

/** Layout constants shared by both clients. */
export const layout = {
  /** Height of the translucent navigation bar. */
  navHeight: 56,
  /** Widest content column (galleries); text columns stop at `textMeasure`. */
  contentMax: 1120,
  textMeasure: 640,
  formMax: 400,
} as const;

/**
 * Motion. One curve family: a long exponential ease-out that arrives softly. Presses scale
 * down a touch; nothing bounces except the like heart.
 */
export const motion = {
  duration: { instant: 120, fast: 180, base: 260, slow: 420, reveal: 640 },
  /** cubic-bezier control points. */
  easeOut: [0.16, 1, 0.3, 1] as const,
  easeInOut: [0.65, 0, 0.35, 1] as const,
  pressScale: 0.97,
  /** For react-native-reanimated / motion springs. */
  spring: { damping: 26, stiffness: 320, mass: 1 },
} as const;

export const cubicBezier = (points: readonly [number, number, number, number]) =>
  `cubic-bezier(${points.join(", ")})`;
